import React from 'react';
import { Box, Container, Typography, Grid, Button, Stack, Chip, alpha } from '@mui/material';
import { School, CheckCircle, Public, Flight, AccountBalance } from '@mui/icons-material';

const countries = [
  {
    name: 'Bangladesh',
    flag: '🇧🇩',
    color: '#006a4e',
    accent: '#f42a41',
    tag: '🎓 SAARC Neighbor | Affordable',
    title: 'MBBS in Bangladesh',
    description: 'Bangladesh has emerged as a preferred destination for Indian students seeking affordable and quality medical education. With NMC-approved universities and a curriculum aligned with international standards, Bangladesh offers a seamless pathway to a medical career.',
    stats: [
      { icon: <School />, value: '35+', label: 'Medical Colleges' },
      { icon: <CheckCircle />, value: 'NMC', label: 'Approved' },
      { icon: <AccountBalance />, value: '$3,000', label: 'Avg Tuition/yr' },
      { icon: <Flight />, value: '1 hr', label: 'Flight from India' },
    ],
    highlights: [
      'No language barrier — English medium instruction',
      'Similar climate & culture to India',
      'High FMGE/USMLE pass rates',
      'Affordable living costs ($150/mo)',
    ],
    image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&q=80',
  },
  {
    name: 'Nepal',
    flag: '🇳🇵',
    color: '#003893',
    accent: '#DC143C',
    tag: '🏔️ Himalayan Neighbor | Trusted',
    title: 'MBBS in Nepal',
    description: 'Nepal offers one of the most accessible MBBS programs for Indian students, with no mandatory donation fees and a curriculum closely aligned with the Indian medical education system. Its proximity and cultural familiarity make it a top choice.',
    stats: [
      { icon: <School />, value: '20+', label: 'Medical Colleges' },
      { icon: <CheckCircle />, value: 'NMC', label: 'Approved' },
      { icon: <AccountBalance />, value: '$4,000', label: 'Avg Tuition/yr' },
      { icon: <Flight />, value: '2 hrs', label: 'Flight from India' },
    ],
    highlights: [
      'No capitation/donation fees required',
      'Indian curriculum alignment',
      'Recognized by WHO, NMC, ECFMG',
      'Scenic campus environments',
    ],
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80',
  },
  {
    name: 'Russia',
    flag: '🇷🇺',
    color: '#0039A6',
    accent: '#D52B1E',
    tag: '🌍 Global Leader | World-Class',
    title: 'MBBS in Russia',
    description: 'Russia is home to some of the world\'s oldest and most prestigious medical universities. With globally recognized degrees, advanced infrastructure, and a strong focus on research, Russian medical education opens doors worldwide.',
    stats: [
      { icon: <School />, value: '50+', label: 'Medical Universities' },
      { icon: <CheckCircle />, value: 'NMC', label: 'Approved' },
      { icon: <AccountBalance />, value: '$3,500', label: 'Avg Tuition/yr' },
      { icon: <Flight />, value: '6 hrs', label: 'Flight from India' },
    ],
    highlights: [
      'Globally recognized degrees (WHO, ECFMG, GMC)',
      'English-medium programs available',
      'Advanced research & clinical exposure',
      'Rich cultural & historical experience',
    ],
    image: 'https://images.unsplash.com/photo-1513326738677-b964603b136d?w=800&q=80',
  },
];

const CountriesSection = () => {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8f9fa' }}>
      <Container maxWidth="xl">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Chip
            label="🌍 TOP DESTINATIONS"
            sx={{
              bgcolor: alpha('#8a211d', 0.1),
              color: '#8a211d',
              fontWeight: 700,
              fontSize: '0.8rem',
              px: 2,
              mb: 2,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2rem', md: '2.8rem' },
              mb: 2,
              color: '#2f2522',
            }}
          >
            MBBS in{' '}
            <Box component="span" sx={{ color: '#8a211d' }}>
              Bangladesh, Nepal & Russia
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: 700,
              mx: 'auto',
              fontWeight: 500,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Explore world-class medical education opportunities at affordable fees with 100% admission assistance
          </Typography>
        </Box>

        {/* Country Cards */}
        <Stack spacing={6}>
          {countries.map((country, index) => (
            <Box
              key={country.name}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
                gap: { xs: 3, md: 5 },
                bgcolor: '#ffffff',
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 4px 30px rgba(0,0,0,0.08)',
                transition: 'all 0.4s ease',
                '&:hover': {
                  boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                  transform: 'translateY(-4px)',
                },
              }}
            >
              {/* Image Side */}
              <Box
                sx={{
                  flex: { md: '0 0 45%' },
                  minHeight: { xs: 280, md: 'auto' },
                  background: `url(${country.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(country.color, 0.7)}, ${alpha(country.accent, 0.5)})`,
                  },
                }}
              >
                {/* Overlay Content */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 4,
                    color: 'white',
                  }}
                >
                  <Typography sx={{ fontSize: '3rem', mb: 1 }}>
                    {country.flag}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.6rem', md: '2rem' },
                      textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }}
                  >
                    {country.name}
                  </Typography>
                </Box>
              </Box>

              {/* Content Side */}
              <Box sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
                {/* Tag */}
                <Chip
                  label={country.tag}
                  size="small"
                  sx={{
                    bgcolor: alpha(country.color, 0.1),
                    color: country.color,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    mb: 2,
                  }}
                />

                {/* Title */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    mb: 2,
                    fontSize: { xs: '1.4rem', md: '1.8rem' },
                    color: '#2f2522',
                  }}
                >
                  {country.title}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    mb: 3,
                    fontSize: '0.95rem',
                  }}
                >
                  {country.description}
                </Typography>

                {/* Stats Grid */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {country.stats.map((stat, i) => (
                    <Grid item xs={6} md={3} key={i}>
                      <Box
                        sx={{
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: alpha(country.color, 0.04),
                          borderRadius: 2,
                          border: `1px solid ${alpha(country.color, 0.1)}`,
                        }}
                      >
                        <Box sx={{ color: country.color, mb: 0.5, fontSize: '1.2rem' }}>
                          {stat.icon}
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 900,
                            fontSize: '1rem',
                            color: country.color,
                            lineHeight: 1.2,
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Highlights */}
                <Stack spacing={1} sx={{ mb: 3 }}>
                  {country.highlights.map((item, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                      <CheckCircle sx={{ color: country.color, fontSize: 18, mt: 0.3, flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: 'text.secondary',
                          fontSize: '0.9rem',
                        }}
                      >
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                {/* CTA */}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<School />}
                  onClick={() => {
                    const contactEl = document.getElementById('contact');
                    if (contactEl) {
                      const headerOffset = 120;
                      const elementPosition = contactEl.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }}
                  sx={{
                    bgcolor: country.color,
                    fontWeight: 800,
                    px: 4,
                    py: 1.5,
                    borderRadius: 50,
                    boxShadow: `0 4px 15px ${alpha(country.color, 0.4)}`,
                    '&:hover': {
                      bgcolor: country.accent,
                      boxShadow: `0 6px 20px ${alpha(country.color, 0.5)}`,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Apply for {country.name}
                </Button>
              </Box>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default CountriesSection;